import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { ItemFragmentOnItem_upsales } from '../../GridButtons/__queries__';
import styled from '@emotion/styled';
import { useI18n, useLogger } from '~Hooks';
import TabButton from './TabButton';
import { Box } from '~Styles';
import Button from '../Button';
import Column from '../Column';
import { getInitialSelectedGroupId } from './utils';
import { useEntryFinder } from '~Components/EntryFinder';
import { FormattedModificationGroup } from '~Hooks/ItemModifications/useItemModifications';

export const REST_TAB_KEY = 'REST';

const TabWrapper = styled(Box)`
  overflow-x: auto;
  width: calc(100% + 64px);
  flex-shrink: 0;
  display: flex;
  justify-content: flex-start;
  position: relative;
  margin-left: -32px;
  margin-right: -32px;
  padding-left: 32px;
  padding-right: 40px;
  scroll-behavior: smooth;
  overflow-y: hidden;

  mask-image: linear-gradient(
    to right,
    rgba(0, 0, 0, 0) 0px,
    rgba(0, 0, 0, 1) 8px,
    rgba(0, 0, 0, 1) calc(100% - 16px),
    rgba(0, 0, 0, 0) 100%
  );
  -webkit-mask-image: linear-gradient(
    to right,
    rgba(0, 0, 0, 0) 0px,
    rgba(0, 0, 0, 1) 8px,
    rgba(0, 0, 0, 1) calc(100% - 8px),
    rgba(0, 0, 0, 0) 100%
  );

  & > *:last-child {
    margin-right: 0;
  }
`;

const Content = styled.div<{ isGridView?: boolean }>`
  display: flex;
  overflow: auto;
  position: absolute;
  width: fit-content;

  height: calc(100% - 218px); // 218px is the height of the button wrapper in the parent

  box-sizing: border-box;
  justify-content: flex-start;
  align-items: flex-start;

  margin-top: 64px; // This because the tab buttons is 60px height
`;

const Items = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  grid-auto-rows: 88px;
  gap: 12px;
  overflow-y: auto;
  overflow-x: hidden;
  align-content: flex-start;
  margin-top: 12px;
  height: calc(100% - 218px);
  padding: 0 8px 64px;
  box-sizing: border-box;

  & > * {
    width: 100%;
    height: 88px;
    margin: 0;
    padding: 0;
    align-self: start;
  }

  &::-webkit-scrollbar {
    display: none;
  }
`;

interface Props {
  modificationGroups: FormattedModificationGroup[];
  upsellGroups?: ItemFragmentOnItem_upsales[];
  selectedUpsales?: Array<{ id: string; title: string }>;
  onSelectVariation: (id: string) => void;
  onSelectUpsell: (id: string, title?: string) => void;
}

/**
 * View displayed when there is atleast one modification group with the field isGridViewEnabled set to true.
 * The modification groups with isGridViewEnabled has their own tab, the rest (inc. all upsells) will be displayed in a single tab called "Rest..." displayed with regular <Column />'s
 */
const Grid: FC<Props> = ({
  modificationGroups,
  upsellGroups,
  selectedUpsales,
  onSelectVariation,
  onSelectUpsell,
}) => {
  const { i18n } = useI18n();
  const findEntryFromItem = useEntryFinder();
  const logger = useLogger('variations/upsells');

  const scrollRef = useRef<HTMLDivElement>(null);

  const initiallySelectedGroupId = getInitialSelectedGroupId(modificationGroups, upsellGroups);

  const [hasScroll, setHasScroll] = useState<boolean>(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(initiallySelectedGroupId);

  useEffect(() => {
    if (scrollRef.current) {
      setHasScroll(scrollRef.current.scrollHeight > scrollRef.current.clientHeight);
    }
  }, [scrollRef.current, selectedGroupId]);

  // Generate the items for the selected group or upsale
  const sortedItems = useMemo(() => {
    const currentGroup =
      modificationGroups.find((vg) => vg.id === selectedGroupId) ||
      upsellGroups?.find((upsell) => upsell.id === selectedGroupId);

    if (!currentGroup) {
      return [];
    }

    // If it's an upsale group, return its items directly
    if ('items' in currentGroup) {
      return currentGroup.items;
    }

    return currentGroup.variations ?? [];
  }, [modificationGroups, selectedGroupId]);

  const groupsWithGridView = modificationGroups.filter((vg) => vg.isGridViewEnabled);

  const preselectedGroup = modificationGroups.find((vg) => vg.id === initiallySelectedGroupId);
  /** If the preselected tab is a removable ingredient group AND it has not gridView - place it first among the tabs */
  if (preselectedGroup?.isRemovableIngredientGroup && !preselectedGroup?.isGridViewEnabled) {
    groupsWithGridView.unshift(
      modificationGroups.find((vg) => vg.id === initiallySelectedGroupId)!,
    );
  }

  const totalGroups = modificationGroups.length + (upsellGroups?.length ?? 0);
  const amountOfGroupsWithoutGridView = totalGroups - groupsWithGridView.length;

  const singleModificationGroupWithoutGridView =
    amountOfGroupsWithoutGridView === 1
      ? modificationGroups.find((vg) => !vg.isGridViewEnabled)
      : null;

  const singleUpsellGroupWithoutGridView =
    amountOfGroupsWithoutGridView === 1 && !singleModificationGroupWithoutGridView
      ? upsellGroups?.[0]
      : null;

  /** Render tabs - The only modificationGroups that get their own tab is the ones with isGridViewEnabled: true
   * If there is only one group (ModificationGroup or UpsellGroup that doesnt have gridView, display that one as its seperate tab instead of in "Rest...")
   */
  const renderTabs = () => (
    <>
      {groupsWithGridView.map((group) => (
        <TabButton
          key={group.id}
          modificationGroup={group}
          onTabClick={setSelectedGroupId}
          isCurrent={selectedGroupId === group.id}
        />
      ))}

      {amountOfGroupsWithoutGridView > 0 && (
        <TabButton
          key={
            singleModificationGroupWithoutGridView?.id ??
            singleUpsellGroupWithoutGridView?.id ??
            REST_TAB_KEY
          }
          modificationGroup={singleModificationGroupWithoutGridView ?? undefined}
          upsale={singleUpsellGroupWithoutGridView ?? undefined}
          onTabClick={setSelectedGroupId}
          isCurrent={
            selectedGroupId ===
            (singleModificationGroupWithoutGridView?.id ??
              singleUpsellGroupWithoutGridView?.id ??
              REST_TAB_KEY)
          }
        />
      )}
    </>
  );

  // Render Items (both variation and upsale items)
  const renderItems = () => (
    <>
      {sortedItems.map((item) => {
        // Check if this is an upsale item
        const isUpsaleItem = 'defaultPrice' in item;

        if (isUpsaleItem) {
          const foundEntry = findEntryFromItem(item.id);

          // Don't render items that is not included in menu
          if (!foundEntry) {
            // Likely a misconfiguration - An item has an upsell that is not included in the menu
            logger.warn(
              `Upsell in POS not rendered, could not find entry for ${item.id}`,
              foundEntry,
            );
            return;
          }

          return (
            <Button
              key={item.id}
              title={i18n(item.titleLang)}
              active={Boolean(selectedUpsales?.some((i) => i.id === item.id))}
              isSingle={false}
              price={foundEntry.price}
              onTouchStart={() =>
                !hasScroll ? onSelectUpsell(item.id, i18n(item.titleLang)) : undefined
              }
              onClick={() =>
                hasScroll ? onSelectUpsell(item.id, i18n(item.titleLang)) : undefined
              }
            />
          );
        }

        const group = modificationGroups.find((vg) => vg.id === selectedGroupId);

        return (
          <Button
            key={item.id}
            title={item.name}
            active={item.selected}
            isSingle={group?.type === 'SINGLE'}
            price={item.price}
            isOutOfStock={item.isOutOfStock}
            onTouchStart={() => (!hasScroll ? onSelectVariation(item.id) : undefined)}
            onClick={() => (hasScroll ? onSelectVariation(item.id) : undefined)}
          />
        );
      })}
    </>
  );

  return (
    <>
      <TabWrapper display="flex">{renderTabs()}</TabWrapper>

      {selectedGroupId === REST_TAB_KEY ? (
        <Content>
          {modificationGroups
            .filter((g) => !g.isGridViewEnabled)
            .map((g) => {
              return (
                <Column key={g.id} modificationGroup={g} onSelect={onSelectVariation} isGridView />
              );
            })}
          {upsellGroups?.map((upsell) => (
            <Column
              key={upsell.id}
              upsell={upsell}
              selectedUpsales={selectedUpsales}
              isGridView
              onSelect={onSelectUpsell}
            />
          ))}
        </Content>
      ) : (
        <Items
          ref={scrollRef}
          onTouchStart={(e) => {
            e.stopPropagation();
          }}>
          {renderItems()}
        </Items>
      )}
    </>
  );
};

export default Grid;
